import { useMemo } from 'react';
import { clamp, zipWith } from 'lodash';

const getWmCoordinatesFromPosition = (position: string): [number, number] => {
  const pos = position.toLowerCase();
  let y = 0.5;
  if (pos.includes('top')) {
    y = 1 / 24;
  } else if (pos.includes('bottom')) {
    y = 1 - 1 / 24;
  }

  let x = 0.5;
  if (pos.includes('left')) {
    x = 1 / 25;
  } else if (pos.includes('right')) {
    x = 1 - 1 / 25;
  }

  return [x, y];
};

const getAbsoluteCoordinate = (
  positionMax,
  positionNormalized,
  positionOffset
) => {
  const initial = positionMax * positionNormalized;
  const coordinate = initial + positionOffset;
  return clamp(coordinate, 0, positionMax);
};

const getRelativeOffset = (positionReferencePoint = '') => {
  const relativeOffsetByReferencePoint = {
    BOTTOM_CENTER: [-50, -100],
    BOTTOM_LEFT: [0, -100],
    BOTTOM_RIGHT: [-100, -100],
    MIDDLE_CENTER: [-50, -50],
    MIDDLE_LEFT: [0, -50],
    MIDDLE_RIGHT: [-100, -50],
    TOP_CENTER: [-50, 0],
    TOP_LEFT: [0, 0],
    TOP_RIGHT: [-100, 0],
  };
  return relativeOffsetByReferencePoint[positionReferencePoint] || [0, 0];
};

/**
 * Compute a reference point's absolute position and relative offset, where the
 * reference point is one of nine positions along the left/center/right and
 * top/middle/bottom of a rectangle with dynamic size. The sum of a reference
 * point's position and it's rectangle's offset describe the expected location
 * of a text watermark rendered by the Frame.io stream service. For more
 * information refer to https://github.com/Frameio/stream#features.
 *
 * ```
 * +----------------------------------+
 * |                                △ |
 * |                                │ |
 * |                          TOP=6px |
 * |                                │ |
 * | ◁──LEFT=18px──────────────────▷│ |
 * |                                ▽ |
 * |                A┄┄┄┄┄┄┄B┄┄┄┄┄┄┄C | ☜ RECT_OFFSET_C=[-100%,0]
 * |                ┆               ┆ |
 * |                D       E       F |
 * |                ┆               ┆ |
 * |                G┄┄┄┄┄┄┄H┄┄┄┄┄┄┄I |
 * +----------------------------------+
 * ```
 *
 /**
 * Arguments for the calculatePosition function
 * @param {string} referencePoint - desired reference point for output computations.
 * @param {[number, number]} positionMax - position upper upper limits.
 * @param {[number, number]} positionNormalized - desired position as a percentage from 0 to 1.
 * @param {[number, number]} positionOffset - optionally provide to shift the output position by this number of pixels, 
 */
type CalculatePositionArgs = {
  /** Desired reference point for output computations */
  referencePoint: string;

  /** Position upper limits */
  positionMax: [number, number];

  /** Desired position as a percentage from 0 to 1 */
  positionNormalized: [number, number];

  /** Optionally provide to shift the output position by this number of pixels */
  positionOffset?: [number, number];
};

/**
 * The result of the calculatePosition function
 * @returns {Object} result
 * @returns {[number, number]} result.position - reference point's pixel coordinates on each axis, for example `[138,6]`.
 * @returns {[number, number]} result.referencePointOffset - relative position of the rectangle's top left corner with
 * respect to the reference point as an integer percentage, for example reference point "top_right" gives the rectangle offset `[-100,0]`.
 */
type CalculatePositionResult = {
  /** Reference point's pixel coordinates on each axis */
  position: [number, number];

  /** Relative position of the rectangle's top left corner with respect to the reference point as a percentage */
  relativeOffset: [number, number];
};

export const useReferencePointLayout = (
  args: CalculatePositionArgs
): CalculatePositionResult => {
  const { positionMax, positionNormalized, positionOffset, referencePoint } =
    args;

  const referencePointRelativeOffset = getRelativeOffset(referencePoint);

  // if the positionNormalized not provided, use same calculation as the backend to derive coordinates from the
  let wmCoordinates = positionNormalized;
  if (positionNormalized[0] === null && positionNormalized[1] === null) {
    wmCoordinates = getWmCoordinatesFromPosition(referencePoint);
  }
  const position = useMemo(
    () =>
      zipWith(
        positionMax,
        wmCoordinates,
        positionOffset,
        getAbsoluteCoordinate
      ) as [number, number],
    [positionMax, wmCoordinates, positionOffset]
  );

  return {
    position,
    relativeOffset: referencePointRelativeOffset,
  };
};
